<template>
  <div
    class="wrapper"
    :style="style">
    <div class="content">
      <div
        class="form-wrapper"
        v-if="allowSignIn">
        <v-window v-model="step">
          <v-window-item :value="1">
            <div class="flip-img-wrapper">
              <v-icon class="user-icon">
                fas fa-3x fa-user-lock
              </v-icon>
            </div>
            <div class="title">
              {{ $t('views.signIn.title', 'Sign In') }}
            </div>
            <v-form
              ref="form"
              v-model="valid"
              @submit.prevent="onSubmit"
              lazy-validation>
              <!--firstname-->
              <v-text-field
                solo
                v-model="signInfo.firstName"
                :rules="[rules.required]"
                :autofocus="true"
                :label="$t('views.signIn.form.firstName', 'First Name*')"
                required />
              <!--lastName-->
              <v-text-field
                solo
                v-model="signInfo.lastName"
                :rules="[rules.required]"
                :autofocus="true"
                :label="$t('views.signIn.form.lastName', 'Last Name*')"
                required />
              <!--email-->
              <v-text-field
                solo
                v-model="signInfo.email"
                :rules="[rules.required, rules.email, rules.recovery]"
                :autofocus="true"
                :label="$t('views.signIn.form.email', 'Email*')"
                append-icon="perm_identity"
                required />
              <!--password-->
              <v-text-field
                type="password"
                solo
                v-model="signInfo.password"
                :rules="[rules.required, rules.passwordPattern]"
                :label="$t('views.signIn.form.password', 'Password*')"
                append-icon="lock"
                required />
              <v-text-field
                type="password"
                solo
                v-model="signInfo.confirmPassword"
                :rules="[rules.required, rules.matchPassword]"
                :label="$t('views.signIn.form.confirmPassword', 'Confirm Password*')"
                append-icon="lock" />
              <div class="label-mandatory">
                {{ $t('views.signIn.mandatory', '* indicates required field') }}
              </div>
              <div>
                <span class="label-confidentiality">
                  {{ $t('views.signIn.label.confidentiality', 'You will receive Wemaik\'s Terms of Service, ' +
                    ' Privacy Policy and Cookie Policy.') }}
                </span>
              </div>
              <br>
              <v-btn
                class="subscribe-button"
                type="submit"
                color="primary"
                :disabled="!valid">
                {{ $t('views.signIn.actions.accept', 'Accept and confirm') }}
              </v-btn>
              <a
                class="link"
                @click="navigate('login')">
                {{ $t('views.common.actions.connectWithExistingAccount', 'Connect with existing account') }}
              </a>
            </v-form>
          </v-window-item>

          <v-window-item :value="2">
            <div class="flip-img-wrapper">
              <v-icon class="user-icon">
                fas fa-3x fa-check-circle
              </v-icon>
            </div>
            <br>
            <div class="message-reset">
              {{ $t('views.signIn.message.sendMailSuccess', 'We sent you a link') }}
            </div>
            <br>
            <div class="message-reset">
              {{ $t('views.signIn.message.verifyMail', 'Please check your inbox and click on the secure link')
              }}
            </div>
            <div class="flex-row">
              <a
                class="link send-mail-link"
                @click="reSend">
                {{ $t('views.signIn.actions.resendLink', 'Resend link') }}
              </a>
            </div>
            <br>
            <div class="message-reset">
              {{ $t('views.signIn.message.verifyMailAlert', 'If you do not receive your message, check in ' +
                'the spam emails or contact us') }}
            </div>
            <br>
            <div class="flex-row">
              <v-spacer />
              <v-btn
                color="#ffdf46"
                @click="navigate('login')"
                round>
                {{ $t('views.signIn.actions.next', 'Next') }}
              </v-btn>
            </div>
          </v-window-item>
          <v-window-item :value="3">
            <div class="flip-img-wrapper">
              <v-icon class="user-icon">
                fas fa-3x fa-check-circle
              </v-icon>
            </div>
            <div
              v-if="invalidToken"
              class="error-message message-validation">
              {{ $t('errors.signIn.invalidToken', 'The link is invalid') }}
            </div>
            <div
              v-if="!invalidToken"
              class="message-validation">
              {{ $t('views.signIn.message.validSignIn', 'Congratulations on your registration in the ' +
                'WEMAIK universe!') }}
            </div>
            <br>
            <div
              class="flex-row"
              v-if="!invalidToken">
              <v-btn
                color="primary"
                class="accept-button"
                @click="navigate('login')">
                {{ $t('views.signIn.message.start', 'Start') }}
              </v-btn>
            </div>
            <div class="flex-row">
              <v-spacer />
              <a
                class="link"
                @click="navigate('login')">
                {{ $t('views.signIn.actions.returnLoginScreen', 'Back to the login screen') }}
              </a>
            </div>
          </v-window-item>
        </v-window>
      </div>
      <div
        class="form-wrapper"
        v-else>
        <div class="flip-img-wrapper">
          <v-icon class="user-icon">
            fas fa-3x fa-user-lock
          </v-icon>
        </div>
        <div class="title">
          {{ $t('views.signIn.title', 'Sign In') }}
        </div>
        <div class="success-message">
          {{ $t('views.signIn.message.signInDisabled', 'Sign in has been disabled on this server.') }}
        </div>
        <a
          class="link"
          @click="navigate('login')">
          {{ $t('views.common.actions.connectWithExistingAccount', 'Connect with existing account') }}
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { rules } from '../services'
import Vue from 'vue'
import { VueReCaptcha } from 'vue-recaptcha-v3'

const defaultBackgroundImage = require('../../../assets/img/login-background.jpg')

Vue.use(VueReCaptcha, {
  siteKey: process.env.VUE_APP_RECAPATCHA,
  loaderOptions: {
    useRecaptchaNet: false
  }
})

export default {
  data () {
    return {
      failedToRecovery: false,
      invalidToken: false,
      step: 1,
      valid: true,
      signInfo: {
        instance: null,
        email: null,
        firstName: null,
        lastName: null,
        password: null,
        confirmPassword: null,
        captchaToken: null
      },
      signInfoToken: {
        instance: null,
        token: null
      },
      rules: {
        ...rules,
        recovery: () => !this.failedToRecovery || this.$t('errors.common.fail', 'An error occurred'),
        matchPassword: value => value === this.signInfo.password || this.$t('errors.common.doesNotMatch', 'The confirm password does not match.')
      }
    }
  },
  computed: {
    ...mapState(['authentication']),
    backgroundImage () {
      return this.authentication.config.backgroundImage || defaultBackgroundImage
    },
    style () {
      return {
        'background-image': `url(${this.backgroundImage})`
      }
    },
    instance: {
      get () {
        if (this.authentication.instances.length > 0) {
          return (this.authentication.instances.filter(i => i.url === this.signInfo.instance)[0] || this.authentication.instances[0]).url
        }
        return this.signInfo.instance || this.authentication.instance
      },
      set (value) {
        this.signInfo.instance = value
      }
    },
    showInstanceSelector () {
      const { config: { alwaysShowInstance }, instances: { length } } = this.authentication
      return alwaysShowInstance ? length > 0 : length > 1
    },
    allowSignIn () {
      return this.authentication && this.authentication.config && this.authentication.config.allowSignIn
    }
  },
  methods: {
    ...mapActions('authentication', [
      'passwordRecovery',
      'signIn',
      'validateSignInToken',
      'loadConfig'
    ]),
    validate () {
      return this.$refs.form.validate()
    },
    resetValidation () {
      this.failedToRecovery = false
      this.validate()
    },
    navigate (name) {
      this.$router.push(name)
    },
    handleResponseError ({ error }) {
      if (!error || !error.response) {
        window.bigfootCore.store.dispatch('notification/error', this.$t('errors.common.fatal', `Fatal error. Please contact administrator`))
      } else if (error.response.status === 400 && error.response.data.i18nKey === 'REGISTER_TOKEN_GENERATION_ERROR') {
        window.bigfootCore.store.dispatch('notification/error', this.$t('errors.common.invalidToken', `Token is invalid`))
      } else if (error.response.status === 400 && error.response.data.i18nKey === 'EXPIRED_REGISTER_TOKEN_ERROR') {
        window.bigfootCore.store.dispatch('notification/error', this.$t('errors.common.expiredToken', `Token expired`))
      } else if (error.response.status === 400 && error.response.data.i18nKey === 'PASSWORD_COMPLEXITY_ERROR') {
        window.bigfootCore.store.dispatch('notification/error', this.$t('errors.common.badPasswordPattern', `the password does not respect the complexity`))
      } else if (error.response.status === 400 && error.response.data.i18nKey === 'INVALID_PARAMETER') {
        window.bigfootCore.store.dispatch('notification/error', this.$t('errors.common.emptyPassword', `Password is null or empty`))
      } else if (error.response.status === 400 && error.response.data.i18nKey === 'PASSWORD_NOT_EQUALS_ERROR') {
        window.bigfootCore.store.dispatch('notification/error', this.$t('errors.common.doesNotMatch', `The confirm password does not match`))
      } else {
        const message = (error && error.response && error.response.data && error.response.data.message) ? error.response.data.message : 'unknown'
        window.bigfootCore.store.dispatch('notification/error', this.$t('errors.resetPassword.generic', { message: message }, `An error occurred : ${message}`))
      }
      throw error
    },
    async reSend () {
      try {
        await this.$recaptchaLoaded()
        // Execute reCAPTCHA with action "login".
        this.signInfo.captchaToken = await this.$recaptcha('signIn')
        await this.signIn({
          ...this.signInfo
        })
        this.step = 2
      } catch (error) {
        this.handleResponseError({
          error
        })
        this.failedToRecovery = true
        this.validate()
      }
    },
    async onSubmit () {
      if (this.validate()) {
        try {
          await this.$recaptchaLoaded()
          // Execute reCAPTCHA with action "login".
          this.signInfo.captchaToken = await this.$recaptcha('signIn')
          await this.signIn({
            ...this.signInfo
          })
          this.step = 2
        } catch (error) {
          this.handleResponseError({
            error
          })
          this.failedToRecovery = true
          this.validate()
        }
      }
    }
  },
  watch: {
    '$route': {
      async handler (to, from) {
        this.loadConfig(to.query.portal)
        if (to.query.instance) {
          this.signInfoToken.instance = to.query.instance
          this.signInfo.instance = to.query.instance
        }
        if (to.query.token) {
          this.signInfoToken.token = to.query.token
          try {
            await this.validateSignInToken(this.signInfoToken)
            this.step = 3
            this.invalidToken = false
          } catch (e) {
            this.step = 3
            this.invalidToken = true
          }
        } else {
          this.invalidToken = true
        }
      },
      immediate: true
    },
    loginInfo: {
      handler () {
        this.resetValidation()
      },
      deep: true
    }
  }
}
</script>

<style lang="scss">
    .grecaptcha-badge {
        z-index: 99;
    }
</style>

<style lang="scss" scoped>
    @import "../assets/login";

    .success-message {
        color: white;
        font-size: 16px;
    }

    .label-confidentiality, .message-reset, .message-validation, .label-mandatory {
        color: white;
    }

    .accept-button {
        text-align: center;
    }

    .send-mail-link {
        padding-top: 10px;
    }

    .subscribe-button {
        display: block;
        margin: 0 auto;
    }

    .title {
        text-align: center;
        color: white;
        font-size: 18px;
        margin-bottom: 20px;
    }
</style>
